import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Grid,
  InputAdornment,
  LinearProgress,
  Chip
} from '@mui/material';
import { CheckCircle, Cancel, Person, Phone, Email, Lock } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { SignUpData } from '../../services/cognitoAuthService.ts';
import { AuthModalView } from './AuthModal.tsx';
import { growSumoService } from '../../services/growSumoService.ts';
import { referralTracker } from '../../utils/referralTracking.ts';

interface SignUpFormProps {
  onViewChange: (view: AuthModalView, data?: { email?: string; password?: string; firstName?: string }) => void;
  onSuccess: (email: string, password: string, firstName: string) => void;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  phoneNumber?: string;
}

interface PasswordRequirement {
  text: string;
  met: boolean;
}

const SignUpForm: React.FC<SignUpFormProps> = ({ onViewChange, onSuccess }) => {
  const { signUp, error, isLoading, clearError } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    //companyName: '',
    phoneNumber: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [passwordStrength, setPasswordStrength] = useState(0);

  const handleInputChange = (field: keyof typeof formData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field error when user starts typing
    if (formErrors[field as keyof FormErrors]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
    clearError();

    // Calculate password strength
    if (field === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
  };

  const calculatePasswordStrength = (password: string): number => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password)) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 12.5;
    if (/[^A-Za-z0-9]/.test(password)) strength += 12.5;
    return Math.min(strength, 100);
  };

  const getPasswordStrengthColor = (strength: number): 'error' | 'warning' | 'info' | 'success' => {
    if (strength < 25) return 'error';
    if (strength < 50) return 'warning';
    if (strength < 75) return 'info';
    return 'success';
  };

  const getPasswordStrengthText = (strength: number): string => {
    if (strength < 25) return 'Weak';
    if (strength < 50) return 'Fair';
    if (strength < 75) return 'Good';
    return 'Strong';
  };

  const formatPhoneNumber = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(event.target.value);
    setFormData(prev => ({ ...prev, phoneNumber: formatted }));
    
    if (formErrors.phoneNumber) {
      setFormErrors(prev => ({ ...prev, phoneNumber: undefined }));
    }
    clearError();
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(formData.password)) {
      errors.password = 'Password must contain uppercase, lowercase, number, and special character';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

/*     if (!formData.phoneNumber.trim()) {
      errors.phoneNumber = '' //'Phone number is required for future communications and/or MFA';
    } else {
      const phoneNumbers = formData.phoneNumber.replace(/\D/g, '');
      if (phoneNumbers.length !== 10) {
        errors.phoneNumber = 'Please enter a valid 10-digit phone number';
      }
    } */
   if (formData.phoneNumber?.trim()) {
    const phoneRegex = /^\+?[0-9\s\-()]{7,15}$/;
    if (!phoneRegex.test(formData.phoneNumber)) {
      errors.phoneNumber = 'Please enter a valid phone number';
    }
  }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

const handleSubmit = async (event: React.FormEvent) => {
  event.preventDefault();
  if (!validateForm()) return;

  try {
    const signUpData: SignUpData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      password: formData.password,
      // phoneNumber added only if provided
    };

    const rawPhone = formData.phoneNumber.trim();
    if (rawPhone) {
      signUpData.phoneNumber = rawPhone.replace(/\D/g, '');
    }

    // Sign up the user first
    await signUp(signUpData);

    // After successful signup, create GrowSumo participant with referral data
    try {
      const referralData = referralTracker.getReferralDataForSignup();

      await growSumoService.trackSignup({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: signUpData.phoneNumber || undefined,
        referralKey: referralData?.referralKey,
        partnerId: referralData?.partnerId,
        campaignId: referralData?.campaignId,
      });

      // Clear referral data after successful attribution
      if (referralData) {
        console.log('🔗 Signup attributed to referral:', referralData);
        referralTracker.clearReferralData();
      }
    } catch (growSumoError) {
      // Log but don't fail the signup process
      console.warn('GrowSumo tracking failed:', growSumoError);
    }

    onSuccess(formData.email, formData.password, formData.firstName);
  } catch {
    // handled by context
  }
};
  const passwordRequirements: PasswordRequirement[] = [
    { text: 'At least 8 characters', met: formData.password.length >= 8 },
    { text: 'One uppercase letter', met: /[A-Z]/.test(formData.password) },
    { text: 'One lowercase letter', met: /[a-z]/.test(formData.password) },
    { text: 'One number', met: /\d/.test(formData.password) },
    { text: 'One special character', met: /[@$!%*?&]/.test(formData.password) }
  ];

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 500, mx: 'auto' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
        Create your account to get started
      </Typography>

      <Grid container spacing={2}>
        {/* First Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="First Name"
            fullWidth
            value={formData.firstName}
            onChange={handleInputChange('firstName')}
            error={!!formErrors.firstName}
            helperText={formErrors.firstName}
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
        </Grid>

        {/* Last Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Last Name"
            fullWidth
            value={formData.lastName}
            onChange={handleInputChange('lastName')}
            error={!!formErrors.lastName}
            helperText={formErrors.lastName}
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
        </Grid>

        {/* Email */}
        <Grid item xs={12}>
          <TextField
            label="Email Address"
            type="email"
            fullWidth
            value={formData.email}
            onChange={handleInputChange('email')}
            error={!!formErrors.email}
            helperText={formErrors.email}
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
        </Grid>

        {/* Company Name */}
{/*         <Grid item xs={12}>
          <TextField
            label="Company Name (Optional)"
            fullWidth
            value={formData.companyName}
            onChange={handleInputChange('companyName')}
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Business color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
        </Grid> */}

        {/* Phone Number */}
        <Grid item xs={12}>
          <TextField
            label="Mobile Phone Number (optional)"
            fullWidth
            value={formData.phoneNumber}
            onChange={handlePhoneChange}
            //error={!!formErrors.phoneNumber}
            helperText={formErrors.phoneNumber} // || 'Required for contact and/or two-factor authentication'}
            disabled={isLoading}
            placeholder="(555) 123-4567"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
        </Grid>

        {/* Password */}
        <Grid item xs={12}>
          <TextField
            label="Password"
            type="password"
            fullWidth
            value={formData.password}
            onChange={handleInputChange('password')}
            error={!!formErrors.password}
            helperText={formErrors.password}
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />

          {formData.password && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                  Password strength:
                </Typography>
                <Chip
                  label={getPasswordStrengthText(passwordStrength)}
                  size="small"
                  color={getPasswordStrengthColor(passwordStrength)}
                />
              </Box>
              <LinearProgress
                variant="determinate"
                value={passwordStrength}
                color={getPasswordStrengthColor(passwordStrength)}
                sx={{ height: 4, borderRadius: 2 }}
              />
              
              <Box sx={{ mt: 2 }}>
                {passwordRequirements.map((req, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    {req.met ? (
                      <CheckCircle sx={{ fontSize: 16, color: 'success.main', mr: 1 }} />
                    ) : (
                      <Cancel sx={{ fontSize: 16, color: 'error.main', mr: 1 }} />
                    )}
                    <Typography variant="caption" color={req.met ? 'success.main' : 'error.main'}>
                      {req.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Grid>

        {/* Confirm Password */}
        <Grid item xs={12}>
          <TextField
            label="Confirm Password"
            type="password"
            fullWidth
            value={formData.confirmPassword}
            onChange={handleInputChange('confirmPassword')}
            error={!!formErrors.confirmPassword}
            helperText={formErrors.confirmPassword}
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
        </Grid>
      </Grid>

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={isLoading}
        sx={{
          mt: 4,
          mb: 3,
          py: 1.5,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #3f51b5 0%, #2196f3 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #303f9f 0%, #1976d2 100%)',
          }
        }}
      >
        {isLoading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          'Create Account'
        )}
      </Button>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Already have an account?{' '}
          <Link
            component="button"
            type="button"
            onClick={() => onViewChange('signin')}
            sx={{ fontWeight: 600, textDecoration: 'none' }}
          >
            Sign in
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default SignUpForm;